import { Subproject } from '@/stores/SubprojectStore';
import { Add, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { SubprojectBreakdown } from './SubprojectBreakdown';
import { CAPITAL_CATEGORIES, LineItemState } from './types';

type CapitalScope = 'fy' | 'total';
const FONT_HEADER = 'Montserrat, sans-serif';

const headerTypographySx = {
  fontFamily: FONT_HEADER,
  fontWeight: 700,
  color: 'text.primary',
  textTransform: 'uppercase',
} as const;

// Column widths shared across header and rows
const COL_LABEL = '1fr';
const COL_AMOUNT = '160px';

// Shared sx for amount TextFields on light-background rows
const amountFieldSx = {
  '& input': { textAlign: 'right', color: 'primary.main' },
  '& .MuiInputAdornment-root': { color: 'primary.main' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(14, 70, 63, 0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(14, 70, 63, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#0E463F', borderWidth: 2 },
  },
  '& legend': { display: 'none' },
  '& .MuiOutlinedInput-notchedOutline': { top: 0 },
} as const;

interface RowProps {
  label: React.ReactNode;
  ownerValue: string;
  contractorValue: string;
  onOwnerChange: (val: string) => void;
  onContractorChange: (val: string) => void;
  action?: React.ReactNode;
  bgcolor?: string;
}

function AmountRow({
  label,
  ownerValue,
  contractorValue,
  onOwnerChange,
  onContractorChange,
  action,
  bgcolor,
}: RowProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${COL_LABEL} ${COL_AMOUNT} ${COL_AMOUNT} 40px`,
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        pl: 6,
        pr: 1,
        borderBottom: '1px solid',
        borderColor: 'rgba(14, 70, 63, 0.2)',
        bgcolor: bgcolor ?? '#D5E4D5',
        color: 'primary.main',
      }}
    >
      <Box>{label}</Box>
      <NumericFormat
        customInput={TextField}
        thousandSeparator={true}
        decimalScale={2}
        fixedDecimalScale={true}
        size='small'
        value={ownerValue}
        onValueChange={(values: any) => onOwnerChange(values.value)}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          },
        }}
        sx={amountFieldSx}
      />
      <NumericFormat
        customInput={TextField}
        thousandSeparator={true}
        decimalScale={2}
        fixedDecimalScale={true}
        size='small'
        value={contractorValue}
        onValueChange={(values: any) => onContractorChange(values.value)}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          },
        }}
        sx={amountFieldSx}
      />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>{action}</Box>
    </Box>
  );
}

interface CapitalCostTableProps {
  lineItems: LineItemState[];
  subprojects: Subproject[];
  fyName?: string;
  onChange: (items: LineItemState[]) => void;
}

export function CapitalCostTable({
  lineItems,
  subprojects,
  fyName,
  onChange,
}: CapitalCostTableProps) {
  const [scope, setScope] = useState<CapitalScope>('fy');

  const ownerField =
    scope === 'fy' ? 'ownerAmountFiscalyear' : 'ownerAmountTotal';
  const contractorField =
    scope === 'fy' ? 'contractorAmountFiscalyear' : 'contractorAmountTotal';
  const fixedItems = CAPITAL_CATEGORIES.map((cat) => {
    const existing = lineItems.find((li) => li.category === cat.category);
    return (
      existing ?? {
        clientId: crypto.randomUUID(),
        section: 'capital' as const,
        category: cat.category,
        label: '',
        ownerAmountFiscalyear: '',
        contractorAmountFiscalyear: '',
        ownerAmountTotal: '',
        contractorAmountTotal: '',
        sortOrder: cat.sortOrder,
        subprojectBreakdowns: [],
      }
    );
  });

  const customItems = lineItems.filter((li) => li.category === 'custom');

  function updateItem(clientId: string, patch: Partial<LineItemState>) {
    const allItems = [...fixedItems, ...customItems];
    onChange(
      allItems.map((item) =>
        item.clientId === clientId ? { ...item, ...patch } : item,
      ),
    );
  }

  function addCustomRow() {
    const nextSortOrder =
      Math.max(
        ...lineItems.map((li) => li.sortOrder),
        CAPITAL_CATEGORIES.length - 1,
      ) + 1;
    const newItem: LineItemState = {
      clientId: crypto.randomUUID(),
      section: 'capital',
      category: 'custom',
      label: '',
      ownerAmountFiscalyear: '',
      contractorAmountFiscalyear: '',
      ownerAmountTotal: '',
      contractorAmountTotal: '',
      sortOrder: nextSortOrder,
      subprojectBreakdowns: [],
    };
    onChange([...fixedItems, ...customItems, newItem]);
  }

  function removeCustomRow(clientId: string) {
    onChange([
      ...fixedItems,
      ...customItems.filter((li) => li.clientId !== clientId),
    ]);
  }

  return (
    <Box>
      {/* Header row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${COL_LABEL} ${COL_AMOUNT} ${COL_AMOUNT} 40px`,
          gap: 1,
          bgcolor: 'background.paper',
          pl: 6,
          pr: 1,
          py: 2,
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Typography variant='caption' sx={headerTypographySx}>
          Capital Cost Estimation
        </Typography>
        <Typography
          variant='caption'
          sx={{ ...headerTypographySx, textAlign: 'center' }}
        >
          Owner
        </Typography>
        <Typography
          variant='caption'
          sx={{ ...headerTypographySx, textAlign: 'center' }}
        >
          Contractor
        </Typography>
        <Box />
      </Box>

      {/* Scope tab bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '2px',
          px: 6,
          pt: 1,
          bgcolor: 'background.paper',
          borderBottom: '2px solid',
          borderColor: '#D5E4D5',
        }}
      >
        {(['fy', 'total'] as CapitalScope[]).map((s) => {
          const label =
            s === 'fy' ? (fyName ?? 'Fiscal Year') : 'Total Project';
          const isActive = scope === s;
          return (
            <Box
              key={s}
              onClick={() => setScope(s)}
              sx={{
                px: 2,
                py: 0.5,
                cursor: 'pointer',
                borderRadius: '4px 4px 0 0',
                bgcolor: isActive ? '#D5E4D5' : 'rgba(255, 255, 255, 0.1)',
                border: '1px solid',
                borderColor: isActive
                  ? 'rgba(14, 70, 63, 0.25)'
                  : 'rgba(255, 255, 255, 0.2)',
                borderBottom: isActive ? '2px solid #D5E4D5' : undefined,
                mb: isActive ? '-2px' : 0,
                transition: 'background-color 0.15s',
                '&:hover': {
                  bgcolor: isActive ? '#D5E4D5' : 'rgba(255, 255, 255, 0.18)',
                },
              }}
            >
              <Typography
                variant='caption'
                sx={{
                  fontFamily: FONT_HEADER,
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? 'primary.main' : 'rgba(213, 228, 213, 0.8)',
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                  userSelect: 'none',
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Fixed rows */}
      {CAPITAL_CATEGORIES.map((cat, index) => {
        const item = fixedItems.find((fi) => fi.category === cat.category)!;
        const rowBgColor = index % 2 === 0 ? '#D5E4D5' : '#CCDCCC';
        const label = (
          <Box>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='body2'>{cat.label}</Typography>
              {cat.optional && (
                <Typography
                  variant='caption'
                  sx={{ color: 'rgba(14, 70, 63, 0.6)', fontStyle: 'italic' }}
                >
                  (Optional)
                </Typography>
              )}
            </Stack>
            {cat.hint && (
              <Typography
                variant='caption'
                component='p'
                sx={{
                  color: 'rgba(14, 70, 63, 0.7)',
                  lineHeight: 1.3,
                  pr: 2,
                }}
              >
                {cat.hint}
              </Typography>
            )}
          </Box>
        );

        return (
          <Box key={cat.category}>
            <AmountRow
              label={label}
              ownerValue={item[ownerField]}
              contractorValue={item[contractorField]}
              onOwnerChange={(val) =>
                updateItem(item.clientId, { [ownerField]: val })
              }
              onContractorChange={(val) =>
                updateItem(item.clientId, { [contractorField]: val })
              }
              bgcolor={rowBgColor}
            />
            {cat.hasBreakdown && subprojects.length > 0 && (
              <SubprojectBreakdown
                ownerTotal={item[ownerField]}
                contractorTotal={item[contractorField]}
                scope={scope}
                subprojects={subprojects}
                breakdowns={item.subprojectBreakdowns}
                onChange={(newBreakdowns) =>
                  updateItem(item.clientId, {
                    subprojectBreakdowns: newBreakdowns,
                  })
                }
              />
            )}
          </Box>
        );
      })}

      {/* Custom rows */}
      {customItems.map((item, index) => (
        <AmountRow
          key={item.clientId}
          label={
            <TextField
              size='small'
              placeholder='Variable name'
              value={item.label}
              onChange={(e) =>
                updateItem(item.clientId, { label: e.target.value })
              }
              fullWidth
              sx={{
                '& input': { color: 'primary.main' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(14, 70, 63, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(14, 70, 63, 0.5)' },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0E463F',
                    borderWidth: 2,
                  },
                },
                '& legend': { display: 'none' },
                '& .MuiOutlinedInput-notchedOutline': { top: 0 },
              }}
            />
          }
          ownerValue={item[ownerField]}
          contractorValue={item[contractorField]}
          onOwnerChange={(val) =>
            updateItem(item.clientId, { [ownerField]: val })
          }
          onContractorChange={(val) =>
            updateItem(item.clientId, { [contractorField]: val })
          }
          action={
            <IconButton
              size='small'
              onClick={() => removeCustomRow(item.clientId)}
              aria-label='Remove row'
            >
              <Close fontSize='small' />
            </IconButton>
          }
          bgcolor={
            (CAPITAL_CATEGORIES.length + index) % 2 === 0
              ? '#D5E4D5'
              : '#CCDCCC'
          }
        />
      ))}

      {/* Add Variable button */}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
        <Button
          startIcon={<Add />}
          size='small'
          onClick={addCustomRow}
          variant='text'
          sx={{
            backgroundColor: 'background.paper',
            color: 'white',
          }}
        >
          Add Variable
        </Button>
      </Box>
    </Box>
  );
}
